import { MutableRefObject } from 'react';
import { generateUniqueId } from './commonUtils';

export class ImageStore {
   private static instance: ImageStore;
   private store: Map<string, any>;

   private constructor() {
      this.store = new Map();
   }

   public static getInstance(): ImageStore {
      if (!ImageStore.instance) {
         ImageStore.instance = new ImageStore();
      }
      return ImageStore.instance;
   }

   public setItem(key: string, value: any): void {
      this.store.set(key, value);
   }

   public getItem(key: string): any {
      return this.store.get(key);
   }

   public removeItem(key: string): void {
      this.store.delete(key);
   }
}

export const closeImageModal = (
   { args, onUserAction, additionalData, getState }: any,
   callback: Function
) => {
   const isCloseWithoutUpload = args?.[0] === 'closeWithoutUpload';
   const { storeValues } = getState();
   const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
   let isSuccess;

   if (isNewSKYCJourney) {
      isSuccess = additionalData?.dataFromWidget?.response?.status === '200';
   } else {
      isSuccess = additionalData?.dataFromWidget?.response?.status?.code === '200';
   }

   isSuccess = isSuccess || isCloseWithoutUpload;

   if (isSuccess) {
      callback();
      if (!isCloseWithoutUpload) {
         onUserAction({
            actions: [
               {
                  type: 'nativeEventEmitter',
                  payload: {
                     eventName: 'step4Widget',
                     eventData: {
                        state: 'success',
                        isExpanded: false,
                     },
                  },
               },
               {
                  type: 'nativeEventEmitter',
                  payload: {
                     eventName: 'step5Widget',
                     eventData: {
                        state: 'none',
                        isExpanded: true,
                     },
                  },
               },
               isNewSKYCJourney && {
                  analyticsAction: 'click',
                  analyticsData: {
                     eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                     section: 'stepsSection',
                     subSection: 'Step5Widget',
                     eventAction: 'pageloaded',
                     eventLabel: '',
                     eventValue: '',
                     customValue: '',
                     customLabel: '',
                     prop35: '{{storeValues:orderData.orderData.cartId}}',
                     extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                     prop45: '{{storeValues:appData.subJourney}}',
                     eVar21: '{{storeValues:appData.loggedInNumber}}',
                     eVar36: '{{storeValues:appData.msisdn}}',
                     city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                     eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                     prop4: 'SKYC',
                     extraData11: '{{storeValues:layoutContext.layout}}',
                     prop7: '{{storeValues:outstationContext.isOutstation}}',
                     prop9: '{{storeValues:appData.lob}}'
                  },
               },
            ],
         });
      }
   } else {
      onUserAction({
         actions: [
            {
               type: 'showToast',
               payload: {
                  title: 'Something went wrong please try again',
                  dismissDuration: 5000,
                  position: 'bottom-center',
                  state: 'error',
                  style: {
                     zIndex: 10000,
                  },
               },
            },
         ],
      });
   }
};

export const handleFaceMatchResponse = (
   props: any,
   faceMatchFailureCallback: (faceAuthMessage: string) => void
) => {
   const { onUserAction, additionalData, getState } = props;
   const response = additionalData?.dataFromWidget?.response;
   const { storeValues } = getState();
   const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
   const DEFAULT_FACE_AUTH_MESSAGE =
      isNewSKYCJourney ? 'To complete verification, it is important that this photo & your Aadhaar photo matches.' : 'To complete re-verification, it is important that this photo & your Aadhaar photo matches.';
   const imageStore = ImageStore.getInstance();
   const waterMarkUserImage = imageStore.getItem('waterMarkUserImage');
   const actions = [];

   let isSuccess;
   let isFaceMatched;
   let isFaceMatchFailureStatus;
   let faceAuthMessage;

   let headers = {};

   if (isNewSKYCJourney) {
      isSuccess = response?.status === '200'; //TODO remove mock
      // isSuccess =  true;
      isFaceMatched = true;
      const failedAuthCheck: any =
         response?.data?.imageValidationResult &&
         Object.keys(response?.data?.imageValidationResult).length > 0 &&
         Object.values(response?.data?.imageValidationResult).find(
            (result: any) => result?.result?.toLowerCase() === 'failed'
         );
      isFaceMatchFailureStatus = !!failedAuthCheck;
      faceAuthMessage = failedAuthCheck?.displayMessage;
   } else {
      isSuccess = response?.status?.code === '200';
      isFaceMatched = response?.result?.is_FACE_MATCHED;
      isFaceMatchFailureStatus = Object.values(response?.result?.faceAuthResults).some(
         (result: any) => result?.status === '2'
      );
      faceAuthMessage = response?.result?.faceAuthMessage;
   }

   faceAuthMessage = faceAuthMessage || DEFAULT_FACE_AUTH_MESSAGE;
   const blockUser = !isFaceMatched || isFaceMatchFailureStatus;

   if (!isSuccess) {
      actions.push(
         {
            type: 'fullPageLoader',
            payload: {
               show: false,
            },
         },
         {
            type: 'showToast',
            payload: {
               title: 'Something went wrong please try again',
               dismissDuration: 5000,
               position: 'bottom-center',
               state: 'error',
               style: {
                  zIndex: 10000,
               },
            },
         }
      );
   } else {
      if (blockUser) {
         actions.push({
            type: 'fullPageLoader',
            payload: {
               show: false,
            },
         });
         faceMatchFailureCallback(faceAuthMessage);
         if (isNewSKYCJourney) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step4Widget',
                  eventAction: 'impression',
                  eventLabel: 'retry',
                  eventValue: 'photo does not match with your aadhar',
                  customValue: faceAuthMessage,
                  customLabel: 'card',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}',
                  eVar10: 'error'
               },
            });
         } 
      } else {
         let apiUrl = 'skyc/ecaf-commons/api/v2/image/upload/image/v1';

         if (isNewSKYCJourney) {
            apiUrl = 'new-acq/dochub/doc-hub/upload/image';
            const circleId = storeValues?.pincodeContext?.pincodeDetails?.circleId;
            const consumerLob = storeValues?.appData?.lob;
            headers = {
               source: 'shop',
               'x-correlation-id': generateUniqueId(),
               'x-consumer-lob': consumerLob,
               circleId
            };
         }

         actions.push({
            type: 'apiCall',
            payload: {
               requestType: 'POST',
               apiUrl,
               authTokenGroup: 'SKYC_REVERIFICATION',
               ...{ headers },
               body: waterMarkUserImage,
               onSuccess: {
                  actions: [
                     {
                        type: 'fullPageLoader',
                        payload: {
                           show: false,
                        },
                     },
                     {
                        payload: {
                           eventData: {
                              state: 'none',
                              isExpanded: true,
                           },
                           eventName: 'step5Widget',
                        },
                        type: 'nativeEventEmitter',
                     },
                     isNewSKYCJourney && {
                        analyticsAction: 'click',
                        analyticsData: {
                           eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                           section: 'stepsSection',
                           subSection: 'Step5Widget',
                           eventAction: 'pageloaded',
                           eventLabel: '',
                           eventValue: '',
                           customValue: '',
                           customLabel: '',
                           prop35: '{{storeValues:orderData.orderData.cartId}}',
                           extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                           prop45: '{{storeValues:appData.subJourney}}',
                           eVar21: '{{storeValues:appData.loggedInNumber}}',
                           eVar36: '{{storeValues:appData.msisdn}}',
                           city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                           eVar25:
                              '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                           prop4: 'SKYC',
                           extraData11: '{{storeValues:layoutContext.layout}}',
                           prop7: '{{storeValues:outstationContext.isOutstation}}',
                           prop9: '{{storeValues:appData.lob}}'
                        },
                     },
                     {
                        payload: {
                           eventData: {
                              state: 'success',
                              isExpanded: false,
                           },
                           eventName: 'step4Widget',
                        },
                        type: 'nativeEventEmitter',
                     },
                     {
                        payload: {
                           args: ['success'],
                           utilName: 'closeImageModal',
                        },
                        type: 'callUtilFunction',
                     },
                  ],
               },
               onError: {
                  actions: [
                     {
                        type: 'fullPageLoader',
                        payload: {
                           show: false,
                        },
                     },
                     {
                        type: 'showToast',
                        payload: {
                           title: 'something went wrong please try again',
                           dismissDuration: 5000,
                           position: 'bottom-center',
                           state: 'error',
                           style: {
                              zIndex: 10000,
                           },
                        },
                     },
                  ],
               },
            },
         });
      }
   }
   onUserAction({
      actions,
   });
};

export const closeCameraModalWithErrorAction = (
   type: 'image' | 'video',
   artistRef?: MutableRefObject<any>
) => {
   const utilName = type === 'image' ? 'closeImageModal' : 'closeVideoModal';
   artistRef?.current?.onUserAction({
      id: 'closeVideoModalAction',
      actions: [
         {
            payload: {
               args: ['closeWithoutUpload'],
               utilName,
            },
            type: 'callUtilFunction',
         },
         {
            type: 'showToast',
            payload: {
               title: 'Allow camera permission \n This is required to take your photo and video for KYC.',
               description: 'Allow camera access from your phone settings to proceed.',
               dismissDuration: 5000,
               position: 'top-center',
               state: 'error',
               style: {
                  zIndex: 10000,
               },
            },
         },
      ],
   });
}; 